"use client";
import { useEffect } from 'react';
import { config as FAConfig } from '@fortawesome/fontawesome-svg-core'
import * as Sentry from "@sentry/nextjs";
import type { Metadata } from "next";
import Image from 'next/image';
import Script from "next/script";
import { PublicEnvScript } from 'next-runtime-env';

import { ApolloWrapper } from "@/components/apollo-provider";
import { Header } from "@/components/header/header";
import Main from '@/components/layouts/main';
import Module from '@/components/layouts/module';
import Modal from "@/components/modal/modal";
import ModalProvider from "@/components/modal-provider";
import PageHeading from '@/components/page-heading';
import ReduxProvider from "@/components/redux-provider";
import ThemedPage from '@/components/themed-page/themed-page';
import UserContextProvider from "@/components/user-context-provider/user-context-provider";

import "./globals.scss";
import '@fortawesome/fontawesome-svg-core/styles.css'
FAConfig.autoAddCss = false

export const metadata: Metadata = {
  metadataBase: new URL("https://theanimator.co.uk/"),
  title: "The Animator",
  description:
    "Create fun hand-drawn animations for free and share with the world!",
};

type ErrorPageProps = {
  error: Error & { digest?: string };
};

export default function RootLayout({ error }: ErrorPageProps) {
  const { message, digest } = error;

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <PublicEnvScript />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        {/*<!-- Global site tag (gtag.js) - Google Analytics -->*/}
        <Script
          async
          src="https://www.googletagmanager.com/gtag/js?id=UA-1365544-10"
        ></Script>
        <Script id="google-analytics">
          {`
              window.dataLayer = window.dataLayer || [];
              function gtag() { window.dataLayer.push(arguments) }
              window.ga = window.ga || function () { (ga.q = ga.q || []).push(arguments) }; ga.l = +new Date;
              gtag('js', new Date());
              gtag('config', 'UA-1365544-10');
              ga('create', 'UA-1365544-10', 'auto');
          `}
        </Script>

        <link rel="manifest" href="/manifest.json" />

        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="application-name" content="The Animator" />
        <meta name="apple-mobile-web-app-title" content="The Animator" />
        <meta name="msapplication-starturl" content="/" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
      </head>
      <body>
        <UserContextProvider value={null}>
          <ModalProvider>
            <ApolloWrapper>
              <ReduxProvider>
                <Header />
                <ThemedPage>
                      <Main>
                        <PageHeading>Oh no! Something broke!</PageHeading>

                        <Module>
                          <div className="split-layout">
                            <div className="split-layout--small">
                              <Image
                                src="/images/villain.jpg"
                                alt=""
                                width={640}
                                height={790}
                                style={{ width: "100%" }}
                              />
                            </div>
                            <div className="split-layout--large text-left">
                              <p>Whoops! Very sorry, something went horribly wrong.</p>
                              <p>You might need to refresh the page to get going again.</p>

                              <p>
                                <strong>{message}</strong>
                              </p>
                              <p>Code: {digest}</p>
                            </div>
                          </div>
                          <pre className="text-xs overflow-x-scroll">{error.stack}</pre>
                        </Module>
                      </Main>
                    </ThemedPage>
                <Modal />
              </ReduxProvider>
            </ApolloWrapper>
          </ModalProvider>
        </UserContextProvider>
      </body>
    </html>
  );
}
